import { useState, useEffect, useMemo } from 'react';
import {
  Shield, LogOut, Search, X, ChevronRight, FileText, Tag,
  Calendar, AlertTriangle, Check, RefreshCw
} from 'lucide-react';
import { filesAPI, groupsAPI, devicesAPI } from '../api';
import FilesList from './FilesList';
import AccessKeysPanel from './AccessKeysPanel';
import InstallAppButton from './InstallAppButton';
import './Dashboard.css';
import './FileEditor.css';
import './DealerDashboard.css';

/**
 * Кабинет дилера (реселлера).
 *
 * Отличие от админского Dashboard: дилер видит ТОЛЬКО файлы своих групп и работает
 * исключительно с ключами доступа — выдать ключ, переименовать, отвязать, отозвать,
 * скопировать ссылку клиенту. Содержимого файла (VPN-ссылок) здесь нет вообще:
 * сервер его дилеру и не отдаёт (GET /files/:name — админский).
 */
function DealerDashboard({ user, onLogout }) {
  const [files, setFiles] = useState([]);
  const [groups, setGroups] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);   // { name, group }
  const [fileSettings, setFileSettings] = useState(null);   // { mode, endDate, maxDevices }
  const [deviceSummary, setDeviceSummary] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileShowEditor, setMobileShowEditor] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  // Счётчик-сигнал панели ключей: растёт по кнопке «Обновить» в шапке, чтобы та
  // перечитала ключи открытого файла, а не только список подписок слева.
  const [keysRefresh, setKeysRefresh] = useState(0);

  useEffect(() => { loadData(); }, []);

  // Ошибка сама гаснет — отдельной кнопки закрытия у дилера нет
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(t);
  }, [error]);

  const loadDeviceSummary = async () => {
    try {
      const res = await devicesAPI.getAll();
      setDeviceSummary(res.data || {});
    } catch {
      // некритично — значки просто не покажутся
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [filesRes, groupsRes] = await Promise.allSettled([
        filesAPI.getAll(),
        groupsAPI.getAll()
      ]);
      if (filesRes.status === 'fulfilled') setFiles(filesRes.value.data.files);
      else setError('Ошибка загрузки списка');
      if (groupsRes.status === 'fulfilled') setGroups(groupsRes.value.data.groups || {});
    } finally {
      setIsLoading(false);
    }
    loadDeviceSummary();
  };

  const handleFileSelect = async (fileName) => {
    const info = files.find(f => f.name === fileName);
    setSelectedFile({ name: fileName, group: info?.group || null });
    setMobileShowEditor(true);
    setFileSettings(null);
    try {
      const res = await filesAPI.getSettings(fileName);
      setFileSettings(res.data);
    } catch (err) {
      setError('Ошибка загрузки настроек: ' + (err.response?.data?.error || err.message));
    }
  };

  // Кнопка в шапке: и список подписок, и ключи открытого файла, и его статус/срок.
  const handleRefreshAll = async () => {
    setKeysRefresh(n => n + 1);
    if (selectedFile) {
      try {
        const res = await filesAPI.getSettings(selectedFile.name);
        setFileSettings(res.data);
      } catch { /* не критично — карточка ключей всё равно обновится */ }
    }
    await loadData();
  };

  const handleClose = () => { setSelectedFile(null); setMobileShowEditor(false); };

  const filteredFiles = files.filter(f =>
    f?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedFiles = useMemo(() => {
    const sorted = [...filteredFiles].sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortBy === 'date') cmp = new Date(a.modified) - new Date(b.modified);
      else if (sortBy === 'lines') cmp = a.linesCount - b.linesCount;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    const result = {};
    for (const gId of Object.keys(groups)) result[gId] = [];
    result['__ungrouped'] = [];
    for (const f of sorted) {
      const g = f.group || '__ungrouped';
      if (!result[g]) result[g] = [];
      result[g].push(f);
    }
    return result;
  }, [filteredFiles, groups, sortBy, sortDir]);

  const initials = user.username?.slice(0, 2).toUpperCase() || 'D';
  const displayName = selectedFile?.name.replace(/\.json$/, '') || '';
  const groupName = selectedFile?.group ? (groups[selectedFile.group]?.name || selectedFile.group) : null;
  const isExpired = fileSettings?.endDate && new Date() > new Date(fileSettings.endDate);

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-logo">
          <div className="header-logo-icon">
            <Shield size={18} />
          </div>
          <span className="header-logo-text">Кабинет дилера</span>
        </div>

        <div className="header-actions">
          <button
            onClick={handleRefreshAll}
            className="btn btn-secondary btn-sm"
            title="Обновить список подписок и ключи открытого файла"
          >
            <RefreshCw size={14} /><span className="btn-label"> Обновить</span>
          </button>
        </div>

        <div className="header-user">
          <div className="header-divider" />
          <InstallAppButton />
          <div className="user-badge">
            <div className="user-avatar">{initials}</div>
            <span className="user-name">{user.name || user.username}</span>
          </div>
          <button onClick={onLogout} className="btn btn-ghost btn-sm btn-icon" title="Выйти">
            <LogOut size={15} />
          </button>
        </div>
      </header>

      <div className={`dashboard-body${mobileShowEditor ? ' mobile-editor-active' : ''}`}>
        <div className="files-panel">
          <div className="panel-header">
            <span className="panel-title">Мои подписки</span>
            <span className="panel-count">{filteredFiles.length}</span>
          </div>

          <div className="search-box">
            <Search size={13} className="search-icon" />
            <input
              type="text"
              placeholder="Поиск..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="search-clear" title="Очистить">
                <X size={12} />
              </button>
            )}
          </div>

          {error && <div className="alert alert-error dashboard-alert">{error}</div>}

          {/* Обработчики удаления/дублирования/групп не передаём — кнопки не появятся */}
          <FilesList
            groupedFiles={groupedFiles}
            groups={groups}
            selectedFile={selectedFile?.name}
            onFileSelect={handleFileSelect}
            isLoading={isLoading}
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
            deviceSummary={deviceSummary}
          />
        </div>

        <button className="mobile-back-btn" onClick={handleClose}>
          <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /> Подписки
        </button>

        <div className="editor-panel">
          {selectedFile ? (
            <div className="file-editor dealer-editor">
              <div className="editor-header">
                <div className="editor-title">
                  <span className="editor-icon"><FileText size={16} /></span>
                  <h3>{displayName}</h3>
                </div>
                <div className="editor-header-actions">
                  {groupName && (
                    <span className="stat-chip"><Tag size={11} /> {groupName}</span>
                  )}
                  <button onClick={handleClose} className="btn btn-ghost btn-sm btn-icon">
                    <X size={15} />
                  </button>
                </div>
              </div>

              {/* Только чтение: срок и статус ставит админ, дилер их видит, но не меняет */}
              <div className="editor-toolbar">
                <div className="editor-stats">
                  {fileSettings && (
                    <>
                      <span className="stat-chip">
                        {fileSettings.mode === 'active'
                          ? <><Check size={11} color="var(--success)" /> Активна</>
                          : <><X size={11} color="var(--danger)" /> Отключена</>}
                      </span>
                      <span className="stat-chip">
                        <Calendar size={11} />
                        {fileSettings.endDate
                          ? new Date(fileSettings.endDate).toLocaleDateString('ru-RU')
                          : 'без срока'}
                      </span>
                      {isExpired && (
                        <span className="stat-chip" style={{ color: 'var(--danger)' }}>
                          <AlertTriangle size={11} /> Срок истёк
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="editor-footer">
                <AccessKeysPanel
                  fileName={selectedFile.name}
                  isAdmin={false}
                  cryptoOnly={user.appType === 'happ'}
                  refreshToken={keysRefresh}
                  onKeysChanged={loadDeviceSummary}
                  onError={setError}
                />
              </div>
            </div>
          ) : (
            <div className="editor-placeholder">
              <div className="placeholder-content">
                <div className="placeholder-icon">
                  <FileText size={28} />
                </div>
                <h3>Выберите подписку</h3>
                <p>слева — ваши подписки, внутри ключи для клиентов</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DealerDashboard;
